import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  CheckSquare,
  Square,
  Mail,
  Play,
  FileText,
  Upload,
  Info,
  Eye,
  CheckCircle2,
  RotateCcw,
  AlertCircle,
  StickyNote,
  RefreshCw,
  ListFilter,
  HelpCircle,
  Calendar,
  Lock,
} from 'lucide-react';
import { ref } from 'firebase/storage';
import {
  ProgramPhaseActionDef,
  ProgramPhaseActionInstance,
  StandardPhaseKey,
  resolvePhaseKey,
} from '../../../types/programPhases';
import type { DocumentMetadata } from '../../../types';
import type { AutomationDefinition } from '../../../types/automation';
import { safeFetch, saveDocumentMetadata, triggerDocumentExtraction } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';
import {
  auth,
  storage,
  uploadBytesWithTimeout,
  getUploadedStoragePath,
  getStorageErrorMessage,
} from '../../../lib/firebase';
import {
  isAcceptableDocumentFileName,
  unsupportedTypeMessage,
  signatureContradiction,
  DOCUMENT_UPLOAD_ACCEPT,
} from '../../../lib/documentFileTypes';
import {
  describeAutomationEffects,
  describeEmailActionEffects,
  type EffectLine,
  type PhaseActionNameLookups,
} from './phaseActionEffects';
import { evaluateTaskVisibility } from '../../../lib/phaseActionVisibility';
import {
  SendConfirmModal,
  ContentViewModal,
  NoteEditModal,
  ActionTaskConfirmModal,
  RollbackTaskModal,
  RetriggerConfirmModal,
  type SendConfirmData,
  type RetriggerConfirmData,
  type ViewFileRef,
} from './PhaseActionModals';

interface AdminActionGatesPanelProps {
  /** RESOLVED application document id (the instances endpoint reads
   *  `applications/{appId}` directly, so a display id would 404). */
  applicationId: string;
  /**
   * The applicant's canonical workflow stage (from the workflow record).
   * This is the phase source of truth: the endpoint's own derivation
   * (`currentStage || stage` on the application doc) is kept only as a
   * fallback for records without a usable stage, because the doc's
   * vocabulary is not guaranteed to match the phase keys.
   */
  currentStage?: string | null;
  /** Applicant display name + contact email from the drawer's record
   *  (the snapshot resolves the email with the same precedence the
   *  completion endpoint's executor uses). Shown in the send
   *  confirmation modal as the exact recipient. */
  applicantName?: string;
  applicantEmail?: string;
  /** The applicant's user id — the owner segment of the sanctioned
   *  document storage path (applications/{userId}/{applicationId}/…). */
  applicantUserId?: string;
  onRefresh?: () => void;
  /**
   * Freshness signal from the drawer: bumped on every successful drawer
   * (re)load. When it changes after mount, the panel refetches so a
   * completion made elsewhere (or an automation event) shows without
   * closing the drawer.
   */
  refreshSignal?: number;
  /**
   * Optimistic stage move in flight (drawer responsiveness fix): the
   * drawer has already confirmed a move to the stage carried by
   * `currentStage`, but the server has not reconciled yet. The panel
   * shows the new phase's header with a "moving" placeholder instead
   * of the previous phase's task list, so the admin sees the move the
   * moment they confirm it — never stale tasks under a new heading.
   */
  stageMovePending?: boolean;
}

const ACTION_ICONS = {
  send_email: Mail,
  send_automation: Play,
  text_input: FileText,
  file_input: Upload,
  visual_text: Info,
  visual_file: Eye,
  select_one_option: ListFilter,
  yes_no: HelpCircle,
  date_entry: Calendar,
};

const DEFAULT_MAX_UPLOAD_MB = 20; // matches ApplicantFlow's MAX_UPLOAD_SIZE

interface UploadedFileState {
  name: string;
  url: string;
  storagePath: string;
}

type ViewState =
  | { kind: 'text'; title: string; text: string }
  | { kind: 'file'; title: string; file: ViewFileRef }
  | null;

/**
 * ADMIN GATES group of the drawer (the APPLICANT GATES group is
 * StageGatePanel): the program's required phase admin tasks for the
 * CURRENT phase, completable where the caseworker works the stage.
 * Completing a required task is what satisfies the stage's admin gate
 * (PR #1194) — this component renders state and posts completions; it
 * never computes the gate itself.
 *
 * One-click controls (drawer spec Part 3, 2026-10-25): send-type
 * tasks run only after a confirmation modal shows exactly what will
 * be sent and what else the automation does; file tasks upload
 * straight onto the application record; display tasks open their
 * content in a viewer popup; record tasks take their note in a popup
 * editor. The completion endpoint's execute-before-stamp semantics
 * are unchanged — these controls only change HOW a completion is
 * initiated and what the admin sees first.
 */
export const AdminActionGatesPanel: React.FC<AdminActionGatesPanelProps> = ({
  applicationId,
  currentStage,
  applicantName,
  applicantEmail,
  applicantUserId,
  onRefresh,
  refreshSignal,
  stageMovePending,
}) => {
  const [instances, setInstances] = useState<ProgramPhaseActionInstance[]>([]);
  const [defs, setDefs] = useState<ProgramPhaseActionDef[]>([]);
  // The phase key the server derived from the application doc. Only a
  // fallback — `resolvePhaseKey` prefers the workflow record's stage.
  const [serverPhaseKey, setServerPhaseKey] = useState<string>('draft');
  const [loading, setLoading] = useState(true);
  // Background refetches (drawer poll, focus refresh, post-completion)
  // must NEVER unmount the mounted panel: the skeleton early-return
  // below is reserved for the first load. A background refresh only
  // flips `refreshing`, which renders a small inline hint while the
  // rows, open modals and their drafts stay exactly where they are.
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [textInputs, setTextInputs] = useState<Record<string, string>>({});
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [rollbackInst, setRollbackInst] = useState<ProgramPhaseActionInstance | null>(null);
  const [retriggerInst, setRetriggerInst] = useState<ProgramPhaseActionInstance | null>(null);
  const [actionConfirmInst, setActionConfirmInst] = useState<ProgramPhaseActionInstance | null>(null);

  // One-click control state.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [confirmInst, setConfirmInst] = useState<ProgramPhaseActionInstance | null>(null);
  const [confirmData, setConfirmData] = useState<SendConfirmData | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmLoadError, setConfirmLoadError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const confirmOpenSeqRef = useRef(0);
  const [viewState, setViewState] = useState<ViewState>(null);
  const [noteInst, setNoteInst] = useState<ProgramPhaseActionInstance | null>(null);
  const [uploads, setUploads] = useState<Record<string, UploadedFileState>>({});
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileTargetRef = useRef<ProgramPhaseActionInstance | null>(null);

  // Lazily fetched name lookups for the send controls + modal (the
  // same three lists ProgramPhasesEditor loads for its pickers).
  const listsRef = useRef<{
    automations?: Promise<AutomationDefinition[]>;
    emailTemplates?: Promise<any[]>;
    docTemplates?: Promise<any[]>;
  }>({});

  // Mirror of the note-editor target, read by fetchInstances so a
  // background refresh never rewrites the text state underneath an
  // open note editor (the field the admin is actively working in).
  const noteInstRef = useRef<ProgramPhaseActionInstance | null>(null);
  useEffect(() => {
    noteInstRef.current = noteInst;
  }, [noteInst]);

  const fetchInstances = async (background = false) => {
    // Only the first load gets the skeleton. Every later fetch —
    // background poll, drawer-signal refetch, post-completion — keeps
    // the mounted rows and modals in place and just swaps the data
    // when it lands, so nothing an admin is mid-way through (a note
    // draft, an open confirm modal, their scroll position) is reset.
    if (background || hasLoadedRef.current) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await safeFetch(`/api/admin/applications/${applicationId}/phase-action-instances`);
      setInstances(data.instances || []);
      setDefs(data.defs || []);
      setServerPhaseKey(data.currentPhaseKey || 'draft');
      setLoadError(null);
      hasLoadedRef.current = true;

      // Populate text input state from the server's saved values —
      // except the instance whose note editor is open right now: its
      // in-progress value is the admin's, not the server's, and a
      // background refresh must not snap it back.
      const txts: Record<string, string> = {};
      (data.instances || []).forEach((inst: ProgramPhaseActionInstance) => {
        if (inst.savedText) txts[inst.id] = inst.savedText;
      });
      const openNoteId = noteInstRef.current?.id;
      setTextInputs((prev) => {
        if (openNoteId && prev[openNoteId] !== undefined) {
          return { ...txts, [openNoteId]: prev[openNoteId] };
        }
        return txts;
      });
    } catch (err: any) {
      console.error('Failed to load phase action instances:', err);
      // A failed BACKGROUND refresh keeps the data already on screen;
      // only a failed first load replaces the panel with the error.
      if (!hasLoadedRef.current) {
        setLoadError(err.message || 'Failed to load admin action gates');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (applicationId) {
      fetchInstances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  // Drawer-driven freshness: refetch when the drawer reloads. Always
  // background — the drawer bumps this signal on its 20s poll and on
  // focus return while the admin may be mid-task in this panel.
  const lastSignalRef = useRef(refreshSignal ?? 0);
  useEffect(() => {
    const s = refreshSignal ?? 0;
    if (s !== lastSignalRef.current) {
      lastSignalRef.current = s;
      if (applicationId) fetchInstances(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const defById = useMemo(() => new Map(defs.map((d) => [d.id, d])), [defs]);

  const loadList = useCallback(
    <T,>(key: 'automations' | 'emailTemplates' | 'docTemplates', url: string, pick: (data: any) => T[]): Promise<T[]> => {
      if (!listsRef.current[key]) {
        listsRef.current[key] = safeFetch(url)
          .then((data: any) => pick(data) || [])
          .catch(() => [] as T[]) as any;
      }
      return listsRef.current[key] as Promise<T[]>;
    },
    []
  );

  const buildLookups = useCallback(async (): Promise<{
    lookups: PhaseActionNameLookups;
    automations: AutomationDefinition[];
    emailTemplates: any[];
  }> => {
    const [automations, emailTemplates, docTemplates] = await Promise.all([
      loadList<AutomationDefinition>('automations', '/api/admin/automations', (d) => d.automations),
      loadList<any>('emailTemplates', '/api/email-templates', (d) => d.templates),
      loadList<any>('docTemplates', '/api/admin/document-templates', (d) => d.templates),
    ]);
    const emailById = new Map(emailTemplates.map((t: any) => [t.id || t.templateId, t]));
    const docById = new Map(docTemplates.map((t: any) => [t.id || t.templateId, t]));
    const lookups: PhaseActionNameLookups = {
      emailTemplate: (id) => {
        const t = emailById.get(id);
        return t ? { name: t.name, subject: t.subject, documentTemplateIds: t.documentTemplateIds } : undefined;
      },
      documentTemplateName: (id) => docById.get(id)?.name,
    };
    return { lookups, automations, emailTemplates };
  }, [loadList]);

  // Names beside the send buttons (best-effort; controls work without).
  const [listNames, setListNames] = useState<{ automations: Map<string, string>; emailTemplates: Map<string, string> } | null>(null);
  useEffect(() => {
    let cancelled = false;
    buildLookups()
      .then(({ automations, emailTemplates }) => {
        if (cancelled) return;
        setListNames({
          automations: new Map(automations.map((a) => [a.id, a.name])),
          emailTemplates: new Map(emailTemplates.map((t: any) => [t.id || t.templateId, t.name])),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Only the application matters here; the lists are workspace-level.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  /**
   * Post a completion. Returns true on success. On failure the task
   * stays incomplete (the endpoint fails closed — a bound send that
   * fails never stamps a completion) and the reason is shown inline
   * on the row with a Retry action.
   */
  const handleComplete = async (instance: ProgramPhaseActionInstance, payload: any = {}): Promise<boolean> => {
    setSubmittingId(instance.id);
    try {
      await safeFetch(
        `/api/admin/applications/${applicationId}/phase-action-instances/${instance.id}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[instance.id];
        return next;
      });
      fetchInstances(true);
      if (onRefresh) onRefresh();
      return true;
    } catch (err: any) {
      setRowErrors((prev) => ({ ...prev, [instance.id]: err.message || 'Unknown error' }));
      return false;
    } finally {
      setSubmittingId(null);
    }
  };

  const handleReopen = async (instance: ProgramPhaseActionInstance, reason: string) => {
    if (!reason.trim()) {
      notifyToast('warning', 'A reason is required to perform a rollback');
      return;
    }
    setSubmittingId(instance.id);
    try {
      await safeFetch(
        `/api/admin/applications/${applicationId}/phase-action-instances/${instance.id}/reopen`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason.trim() }),
        }
      );
      setRollbackInst(null);
      fetchInstances(true);
      if (onRefresh) onRefresh();
    } catch (err: any) {
      notifyToast('error', `Failed to rollback action: ${err.message || 'Unknown error'}`);
    } finally {
      setSubmittingId(null);
    }
  };

  // ── Retrigger (re-send / re-run a completed send task) ────────────
  // The task stays completed; the endpoint enqueues a NEW backend run
  // and refuses (409) while one is queued or running — the error then
  // lands on the row, same as a failed completion.
  const handleRetrigger = async (instance: ProgramPhaseActionInstance) => {
    setSubmittingId(instance.id);
    try {
      await safeFetch(
        `/api/admin/applications/${applicationId}/phase-action-instances/${instance.id}/retrigger`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }
      );
      setRetriggerInst(null);
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[instance.id];
        return next;
      });
      fetchInstances();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      setRetriggerInst(null);
      setRowErrors((prev) => ({ ...prev, [instance.id]: err.message || 'The re-send could not be queued.' }));
    } finally {
      setSubmittingId(null);
    }
  };

  // What a retrigger would send, in the modal's terms: the bound
  // template / automation name (for a decision task, the chosen
  // option's binding), falling back to the server's recorded label.
  const retriggerDataFor = (instance: ProgramPhaseActionInstance): RetriggerConfirmData => {
    const state = instance.sendRunState;
    const kind = state?.kind || (instance.actionType === 'send_automation' ? 'automation' : 'email');
    const def = defs.find((d) => d.id === instance.actionDefId);
    let boundName: string | undefined;
    if (instance.actionType === 'select_one_option') {
      const option = (def?.config?.options || []).find((o) => o.id === instance.selectedOptionId);
      if (option?.actionType === 'send_email' && option.emailTemplateId) {
        boundName = listNames?.emailTemplates.get(option.emailTemplateId);
      } else if (option?.actionType === 'send_automation' && option.automationId) {
        boundName = listNames?.automations.get(option.automationId);
      }
    } else if (kind === 'automation') {
      boundName =
        def?.config?.automationName ||
        (def?.config?.automationId ? listNames?.automations.get(def.config.automationId) : undefined);
    } else if (def?.config?.emailTemplateId) {
      boundName = listNames?.emailTemplates.get(def.config.emailTemplateId);
    }
    return {
      taskTitle: instance.title,
      kind,
      boundName: boundName || state?.label,
      applicantName,
      recipientEmail: state?.recipient || applicantEmail,
      previousError: state?.status === 'failed' ? state.error : undefined,
    };
  };

  // ── Send confirmation (spec 3.1) ──────────────────────────────────

  const openSendConfirm = async (instance: ProgramPhaseActionInstance) => {
    const seq = ++confirmOpenSeqRef.current;
    setConfirmInst(instance);
    setConfirmData(null);
    setConfirmLoadError(null);
    setConfirmLoading(true);
    const def = defById.get(instance.actionDefId);
    try {
      const { lookups, automations } = await buildLookups();
      if (confirmOpenSeqRef.current !== seq) return; // superseded / closed
      const warnings: string[] = [];
      let effects: EffectLine[] = [];
      let boundName: string | undefined;
      if (instance.actionType === 'send_automation') {
        const automationId = def?.config?.automationId || '';
        const automation = automations.find((a) => a.id === automationId);
        if (!def) {
          warnings.push('This task’s configuration could not be loaded, so its effects cannot be previewed.');
        } else if (!automation) {
          boundName = def.config?.automationName || undefined;
          warnings.push(
            'The automation chosen for this task could not be found in this workspace (it may have been archived). Completing the task will fail until the task is re-pointed to a published automation.'
          );
        } else {
          boundName = automation.name;
          effects = describeAutomationEffects(automation, lookups);
          if (automation.status !== 'published') {
            warnings.push(
              `This automation is not published (status: ${automation.status || 'unknown'}). Running it will fail — publish the automation first.`
            );
          }
        }
      } else {
        // send_email
        if (!def) {
          warnings.push('This task’s configuration could not be loaded, so its effects cannot be previewed.');
        } else {
          effects = describeEmailActionEffects(def, lookups);
          const tpl = def.config?.emailTemplateId ? lookups.emailTemplate(def.config.emailTemplateId) : undefined;
          boundName = tpl?.name || undefined;
          if (def.config?.emailTemplateId && !tpl) {
            warnings.push('The email template chosen for this task could not be found in this workspace. Completing the task will fail until another template is chosen.');
          }
        }
      }
      if (!applicantEmail) {
        warnings.push('No email address is on file for this applicant, so the send will fail until one is added.');
      }
      setConfirmData({
        taskTitle: instance.title,
        kind: instance.actionType as 'send_automation' | 'send_email',
        boundName,
        applicantName,
        recipientEmail: applicantEmail,
        effects,
        warnings,
      });
    } catch (err: any) {
      if (confirmOpenSeqRef.current !== seq) return;
      setConfirmLoadError(`Could not load what this task will do (${err.message || 'unknown error'}). Nothing has been sent.`);
    } finally {
      if (confirmOpenSeqRef.current === seq) setConfirmLoading(false);
    }
  };

  const closeSendConfirm = () => {
    confirmOpenSeqRef.current++;
    setConfirmInst(null);
    setConfirmData(null);
    setConfirmLoadError(null);
    setConfirmLoading(false);
  };

  const handleConfirmSend = async () => {
    if (!confirmInst || confirming) return;
    setConfirming(true);
    const ok = await handleComplete(confirmInst);
    setConfirming(false);
    // Success or failure, the modal closes: on success the row flips
    // to its completed state; on failure the row shows the reason
    // inline with a Retry action (spec 3.1).
    closeSendConfirm();
    return ok;
  };

  // ── File upload (spec 3.2) ────────────────────────────────────────

  const pickFileFor = (instance: ProgramPhaseActionInstance) => {
    const def = defById.get(instance.actionDefId);
    const formats = def?.config?.acceptedFormats;
    if (fileInputRef.current) {
      fileInputRef.current.accept =
        Array.isArray(formats) && formats.length > 0
          ? formats.map((f) => (String(f).startsWith('.') ? String(f) : `.${f}`)).join(',')
          : DOCUMENT_UPLOAD_ACCEPT;
    }
    fileTargetRef.current = instance;
    fileInputRef.current?.click();
  };

  const checkSignature = (file: File): Promise<boolean> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const arr = new Uint8Array(reader.result as ArrayBuffer);
        resolve(!signatureContradiction(arr, file.name));
      };
      reader.onerror = () => resolve(true);
      reader.readAsArrayBuffer(file.slice(0, 16));
    });

  const uploadForInstance = async (instance: ProgramPhaseActionInstance, file: File) => {
    const def = defById.get(instance.actionDefId);
    const fail = (message: string) => setRowErrors((prev) => ({ ...prev, [instance.id]: message }));
    try {
      const formats = def?.config?.acceptedFormats;
      if (Array.isArray(formats) && formats.length > 0) {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const allowed = formats.map((f) => String(f).replace(/^\./, '').toLowerCase());
        if (!allowed.includes(ext)) {
          fail(`This task accepts: ${formats.join(', ')}.`);
          return;
        }
      } else if (!isAcceptableDocumentFileName(file.name)) {
        fail(unsupportedTypeMessage(file.name));
        return;
      }
      const maxMb = def?.config?.maxSizeMb || DEFAULT_MAX_UPLOAD_MB;
      if (file.size > maxMb * 1024 * 1024) {
        fail(`File must be under ${maxMb}MB.`);
        return;
      }
      if (!(await checkSignature(file))) {
        fail(`The file's content does not match its file type.`);
        return;
      }
      if (!applicantUserId) {
        fail('The applicant record id is unavailable, so the file cannot be uploaded from here.');
        return;
      }

      setUploadingId(instance.id);
      // The sanctioned admin/staff upload path (the same one the
      // guided document upload uses): Firebase Storage under
      // applications/{ownerUid}/{applicationId}/{type}/{docId}_{name},
      // then the metadata write through /api/applications/documents/
      // save (saveDocumentMetadata). The document type is the task
      // title, which is what attributes the upload to this task on
      // the application record; the instance itself records the file
      // via savedFileUrl/savedFileName when the task is completed.
      const docId = 'doc_' + Math.random().toString(36).substring(2, 11);
      const docType = instance.title;
      const intendedPath = `applications/${applicantUserId}/${applicationId}/${docType}/${docId}_${file.name}`;
      const snapshot = await uploadBytesWithTimeout(ref(storage, intendedPath), file, 60000, 3);
      const downloadUrl = snapshot?.downloadUrl || snapshot?.downloadURL || '';
      const storagePath = getUploadedStoragePath(snapshot, intendedPath);
      const docMeta: DocumentMetadata = {
        docId,
        applicationId,
        userId: applicantUserId,
        name: file.name,
        type: docType,
        uploadDate: new Date().toISOString(),
        status: 'Pending',
        storagePath,
        downloadUrl,
        uploadedBy: auth?.currentUser?.uid || undefined,
      };
      await saveDocumentMetadata(applicationId, docId, docMeta);
      triggerDocumentExtraction(docId, applicationId);
      setUploads((prev) => ({ ...prev, [instance.id]: { name: file.name, url: downloadUrl, storagePath } }));
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[instance.id];
        return next;
      });
    } catch (err: any) {
      fail(getStorageErrorMessage(err) || err.message || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const inst = fileTargetRef.current;
    fileTargetRef.current = null;
    if (file && inst) uploadForInstance(inst, file);
  };

  // ── View / note popups (spec 3.3) ────────────────────────────────

  const openViewFor = (instance: ProgramPhaseActionInstance) => {
    const def = defById.get(instance.actionDefId);
    if (instance.actionType === 'visual_file') {
      const url = def?.config?.referenceFileUrl;
      const name = def?.config?.referenceFileName || instance.title;
      setViewState({
        kind: 'file',
        title: instance.title,
        file: url
          ? url.startsWith('http')
            ? { name, url }
            : { name, storagePath: url }
          : { name },
      });
      return;
    }
    if (instance.actionType === 'visual_text') {
      setViewState({ kind: 'text', title: instance.title, text: def?.config?.displayText || instance.instructions || '' });
      return;
    }
    if (instance.savedText) {
      setViewState({ kind: 'text', title: instance.title, text: instance.savedText });
      return;
    }
    if (instance.savedFileUrl || instance.savedFileName) {
      setViewState({
        kind: 'file',
        title: instance.title,
        file: { name: instance.savedFileName || 'Uploaded file', url: instance.savedFileUrl },
      });
    }
  };

  const handleNoteSave = async (text: string) => {
    if (!noteInst) return;
    setTextInputs((prev) => ({ ...prev, [noteInst.id]: text }));
    const ok = await handleComplete(noteInst, { savedText: text });
    if (ok) setNoteInst(null);
  };

  /** The primary control for a row, dispatched by task type. */
  const handlePrimaryAction = (instance: ProgramPhaseActionInstance) => {
    const def = defById.get(instance.actionDefId);
    const prereqDefIds = def?.prerequisiteActionDefIds || [];
    const uncompletedPrereqs = prereqDefIds
      .map((pId) => {
        const pInst = instances.find((i) => i.actionDefId === pId);
        const pDef = defs.find((d) => d.id === pId);
        if (!pInst || pInst.status !== 'completed') {
          return pInst?.title || pDef?.title || 'Prerequisite Task';
        }
        return null;
      })
      .filter(Boolean);

    if (uncompletedPrereqs.length > 0) {
      notifyToast(
        'warning',
        `Task is blocked. Complete the following prerequisite task(s) first: ${uncompletedPrereqs.join(', ')}`
      );
      return;
    }

    switch (instance.actionType) {
      case 'send_automation':
      case 'send_email':
        openSendConfirm(instance);
        break;
      case 'text_input':
        setNoteInst(instance);
        break;
      case 'file_input':
        if (uploads[instance.id]) {
          setActionConfirmInst(instance);
        } else {
          pickFileFor(instance);
        }
        break;
      case 'select_one_option':
      case 'yes_no':
      case 'date_entry':
        setActionConfirmInst(instance);
        break;
      default:
        setActionConfirmInst(instance);
    }
  };

  // The phase to show: the workflow record's stage (passed in by the
  // drawer) wins over the server's application-doc derivation.
  const currentPhaseKey: StandardPhaseKey = resolvePhaseKey(currentStage, serverPhaseKey);

  // Filter instances for current phase, in the configured sequence:
  // the definition sortOrder is the order the administrator works in
  // (drawer spec addendum item 11 — the panel used to render mint /
  // fetch order, scrambling the step-by-step flow).
  const sortKeyOf = (inst: ProgramPhaseActionInstance) => defById.get(inst.actionDefId)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
  // Conditional visibility (2026-10-27): instances whose definition
  // carries a showWhen condition are rendered only while the shared
  // evaluator — the same one the server gate fold uses — derives them
  // visible from the sibling instances' stored answers. Hidden tasks
  // are not rendered and do not count in the progress chip; broken
  // conditions fail open (visible), so nothing silently disappears.
  const instancesByDefId = new Map(instances.map((i) => [i.actionDefId, i]));
  const isVisible = (inst: ProgramPhaseActionInstance) => {
    const def = defById.get(inst.actionDefId);
    if (!def?.showWhen) return true;
    return evaluateTaskVisibility(def, defById, instancesByDefId).visible;
  };
  const currentPhaseInstances = instances
    .filter((i) => i.phaseKey === currentPhaseKey)
    .filter(isVisible)
    .slice()
    .sort((a, b) => sortKeyOf(a) - sortKeyOf(b));
  const completedCount = currentPhaseInstances.filter((i) => i.status === 'completed').length;

  if (loading) {
    return (
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs px-4 py-3" aria-busy="true">
        <div className="h-4 w-32 bg-slate-100 rounded animate-pulse" />
        <div className="mt-3 space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-11 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
        <span className="sr-only">Loading Phase Action Gates…</span>
      </div>
    );
  }

  // A failed load must not render as "no gates configured" (the panel
  // returns null for a genuinely empty phase) — show the failure + retry.
  if (loadError) {
    return (
      <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-xs font-semibold text-rose-700">
          <AlertCircle size={14} />
          Couldn't load admin action gates — {loadError}
        </span>
        <button
          type="button"
          onClick={() => fetchInstances()}
          className="px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-700 text-xs font-bold hover:bg-rose-100 transition-colors cursor-pointer shrink-0"
        >
          Retry
        </button>
      </div>
    );
  }

  // Optimistic stage move in flight: the drawer already shows the new
  // stage, but the instances on hand still belong to the previous
  // phase. Render the section shell under the NEW phase key with a
  // compact "moving" placeholder — the admin sees the move instantly,
  // and never sees the old phase's tasks listed under the new phase
  // or a skeleton flash while the server reconciles.
  if (stageMovePending) {
    return (
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs px-4 py-3" aria-busy="true">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <CheckCircle2 size={14} className="text-indigo-600" />
            Admin gates
          </h4>
          <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full uppercase">
            {currentPhaseKey}
          </span>
        </div>
        <p className="mt-2.5 flex items-center gap-2 text-xs font-semibold text-slate-500">
          <RefreshCw size={13} className="animate-spin text-indigo-500" />
          Moving to this phase…
        </p>
      </div>
    );
  }

  if (currentPhaseInstances.length === 0) {
    return null;
  }

  const secondaryBtn =
    'px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer shrink-0 inline-flex items-center gap-1.5';
  const primaryBtn =
    'px-3 py-1.5 rounded-lg bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary-dark disabled:opacity-50 cursor-pointer shrink-0 inline-flex items-center gap-1.5';

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs px-4 py-3">
      <input type="file" ref={fileInputRef} className="hidden" onChange={onFileChosen} aria-hidden="true" tabIndex={-1} />
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
          <CheckCircle2 size={14} className="text-indigo-600" />
          Admin gates
        </h4>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold tabular-nums ${
              completedCount === currentPhaseInstances.length
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-800'
            }`}
            aria-label={`${completedCount} of ${currentPhaseInstances.length} admin gates completed`}
          >
            {completedCount}/{currentPhaseInstances.length} done
          </span>
          <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full uppercase">
            {currentPhaseKey}
          </span>
          {refreshing && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400" role="status">
              <RefreshCw size={11} className="animate-spin" />
              Refreshing
            </span>
          )}
        </div>
      </div>

      <div className="mt-2.5 space-y-2">
        {currentPhaseInstances.map((inst) => {
          const def = defs.find((d) => d.id === inst.actionDefId);
          const Icon = ACTION_ICONS[inst.actionType] || Info;
          const isCompleted = inst.status === 'completed';

          const prereqDefIds = def?.prerequisiteActionDefIds || [];
          const uncompletedPrereqs = prereqDefIds
            .map((pId) => {
              const pInst = instances.find((i) => i.actionDefId === pId);
              const pDef = defs.find((d) => d.id === pId);
              if (!pInst || pInst.status !== 'completed') {
                return pInst?.title || pDef?.title || 'Prerequisite Task';
              }
              return null;
            })
            .filter((title): title is string => Boolean(title));

          const isBlocked = !isCompleted && uncompletedPrereqs.length > 0;

          const busy = submittingId === inst.id;
          const uploading = uploadingId === inst.id;
          const rowError = rowErrors[inst.id];
          const upload = uploads[inst.id];
          const executedEntry = (inst.history || []).filter((h) => h.action === 'executed').pop();
          const isSendType = inst.actionType === 'send_automation' || inst.actionType === 'send_email';
          const boundName = isCompleted
            ? undefined
            : inst.actionType === 'send_automation'
              ? def?.config?.automationName || (def?.config?.automationId ? listNames?.automations.get(def.config.automationId) : undefined)
              : inst.actionType === 'send_email' && def?.config?.emailTemplateId
                ? listNames?.emailTemplates.get(def.config.emailTemplateId)
                : undefined;

          return (
            <div
              key={inst.id}
              className={`rounded-xl border px-3 py-2.5 ${
                isCompleted
                  ? 'border-emerald-200 bg-emerald-50/40'
                  : isBlocked
                    ? 'border-amber-200 bg-amber-50/30'
                    : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <button
                  type="button"
                  disabled={isCompleted || isBlocked || busy || uploading}
                  onClick={() => handlePrimaryAction(inst)}
                  className="mt-0.5 text-indigo-600 hover:text-indigo-800 cursor-pointer disabled:opacity-50 shrink-0"
                  aria-label={isCompleted ? `${inst.title} completed` : `Complete ${inst.title}`}
                >
                  {isCompleted ? (
                    <CheckSquare size={17} className="text-emerald-600" />
                  ) : isBlocked ? (
                    <Lock size={16} className="text-amber-600" />
                  ) : (
                    <Square size={17} className="text-slate-400 hover:text-indigo-600" />
                  )}
                </button>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Icon size={13} className="text-slate-400 shrink-0" aria-hidden="true" />
                    <strong className="text-xs font-bold text-slate-800">{inst.title}</strong>
                    {inst.required && (
                      <span className="text-[11px] font-black uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                        Required
                      </span>
                    )}
                    {def?.actionType && def.actionType !== inst.actionType && (
                      <span className="sr-only">{def.actionType}</span>
                    )}
                  </div>
                  {inst.instructions && (
                    <p className="text-[13px] leading-snug text-slate-500 mt-0.5">{inst.instructions}</p>
                  )}

                  {/* Blocker Banner */}
                  {isBlocked && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-amber-900 bg-amber-100/70 px-2.5 py-1 rounded-lg border border-amber-200">
                      <Lock size={12} className="shrink-0 text-amber-700" />
                      <span>Blocked: Complete prerequisite task(s) first ({uncompletedPrereqs.join(', ')})</span>
                    </div>
                  )}

                  {/* One-click controls (spec Part 3) */}
                  {!isCompleted && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {isSendType && (
                        <>
                          <button
                            type="button"
                            onClick={() => openSendConfirm(inst)}
                            disabled={busy || isBlocked}
                            className={primaryBtn}
                          >
                            {inst.actionType === 'send_automation' ? <Play size={13} /> : <Mail size={13} />}
                            {inst.actionType === 'send_automation' ? 'Run' : 'Send'}
                          </button>
                          {boundName && <span className="text-xs text-slate-500 truncate">{boundName}</span>}
                        </>
                      )}
                      {inst.actionType === 'file_input' && (
                        <>
                          <button
                            type="button"
                            onClick={() => pickFileFor(inst)}
                            disabled={busy || uploading || isBlocked}
                            className={primaryBtn}
                          >
                            <Upload size={13} />
                            {uploading ? 'Uploading…' : upload ? 'Replace file' : 'Upload file'}
                          </button>
                          {upload && (
                            <>
                              <span className="text-xs font-semibold text-slate-700 truncate">{upload.name}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  setViewState({
                                    kind: 'file',
                                    title: inst.title,
                                    file: { name: upload.name, url: upload.url || undefined, storagePath: upload.url ? undefined : upload.storagePath },
                                  })
                                }
                                className={secondaryBtn}
                              >
                                <Eye size={13} /> View
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleComplete(inst, { savedFileUrl: upload.url, savedFileName: upload.name })
                                }
                                disabled={busy || isBlocked}
                                className={secondaryBtn}
                              >
                                <CheckSquare size={13} /> Complete with this file
                              </button>
                            </>
                          )}
                        </>
                      )}
                      {(inst.actionType === 'visual_text' || inst.actionType === 'visual_file') && (
                        <button type="button" onClick={() => openViewFor(inst)} disabled={busy} className={secondaryBtn}>
                          <Eye size={13} /> View
                        </button>
                      )}
                      {inst.actionType === 'text_input' && (
                        <button type="button" onClick={() => setNoteInst(inst)} disabled={busy || isBlocked} className={primaryBtn}>
                          <StickyNote size={13} />
                          {textInputs[inst.id]?.trim() ? 'Edit note' : 'Add note'}
                        </button>
                      )}
                      {(inst.actionType === 'select_one_option' || inst.actionType === 'yes_no' || inst.actionType === 'date_entry') && (
                        <button
                          type="button"
                          onClick={() => setActionConfirmInst(inst)}
                          disabled={busy || isBlocked}
                          className={primaryBtn}
                        >
                          <CheckCircle2 size={13} /> Complete Action
                        </button>
                      )}
                    </div>
                  )}

                  {/* Inline failure: the task stays incomplete and the
                      reason from the endpoint is shown with a retry. */}
                  {!isCompleted && rowError && (
                    <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-rose-50 border border-rose-200 px-2.5 py-1.5">
                      <span className="flex items-start gap-1.5 text-xs font-semibold text-rose-700 min-w-0">
                        <AlertCircle size={13} className="shrink-0 mt-px" />
                        <span>{rowError}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => handlePrimaryAction(inst)}
                        disabled={busy || uploading}
                        className="px-2.5 py-1 rounded-md bg-white border border-rose-200 text-rose-700 text-[11px] font-bold hover:bg-rose-100 disabled:opacity-50 cursor-pointer shrink-0 inline-flex items-center gap-1"
                      >
                        <RefreshCw size={11} /> Retry
                      </button>
                    </div>
                  )}

                  {/* Completion Metadata Display */}
                  {isCompleted && (
                    <div className="mt-1 text-xs text-emerald-800 space-y-0.5">
                      <p>
                        Completed by <strong>{inst.completedByName}</strong>
                        {inst.completedAt
                          ? ` on ${new Date(inst.completedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}`
                          : ''}
                      </p>
                      {inst.selectedOptionLabel && (
                        <p className="font-extrabold text-indigo-900">
                          Selected Option: {inst.selectedOptionLabel}
                        </p>
                      )}
                      {inst.savedAnswer && (
                        <p className="font-extrabold text-emerald-900">
                          Answer: {inst.savedAnswer.toUpperCase()}
                        </p>
                      )}
                      {inst.savedDate && (
                        <p className="font-extrabold text-slate-900">
                          Date Entered: {inst.savedDate}
                        </p>
                      )}
                      {executedEntry?.reason && <p className="font-medium text-slate-600">{executedEntry.reason}</p>}
                      {/* Backend send lifecycle + retrigger (2026-10-27):
                          the run state comes from the server's run
                          record; the task stays completed throughout. */}
                      {inst.sendRunState && (
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                              inst.sendRunState.status === 'sent'
                                ? 'bg-emerald-100 text-emerald-700'
                                : inst.sendRunState.status === 'failed'
                                  ? 'bg-rose-100 text-rose-700'
                                  : inst.sendRunState.status === 'running'
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'bg-slate-200 text-slate-600'
                            }`}
                          >
                            {inst.sendRunState.kind === 'email' ? <Mail size={10} /> : <Play size={10} />}
                            {inst.sendRunState.status === 'queued' &&
                              (inst.sendRunState.kind === 'email' ? 'Email queued' : 'Automation queued')}
                            {inst.sendRunState.status === 'running' &&
                              (inst.sendRunState.kind === 'email' ? 'Sending…' : 'Running…')}
                            {inst.sendRunState.status === 'sent' &&
                              (inst.sendRunState.kind === 'email' ? 'Email sent' : 'Automation done')}
                            {inst.sendRunState.status === 'failed' &&
                              (inst.sendRunState.kind === 'email' ? 'Email failed' : 'Automation failed')}
                            {inst.sendRunState.retrigger && inst.sendRunState.status === 'sent' ? ' · re-send' : ''}
                          </span>
                          {(inst.sendRunState.status === 'queued' || inst.sendRunState.status === 'running') && (
                            <span className="text-xs text-slate-500">
                              {inst.sendRunState.status === 'queued'
                                ? 'Queued — runs in the background'
                                : 'Running in the background'}
                            </span>
                          )}
                          {inst.sendRunState.status === 'failed' && inst.sendRunState.error && (
                            <span className="text-xs font-semibold text-rose-700">{inst.sendRunState.error}</span>
                          )}
                          {inst.sendRunState.retriggerable && (
                            <button
                              type="button"
                              onClick={() => setRetriggerInst(inst)}
                              disabled={busy}
                              className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50 cursor-pointer inline-flex items-center gap-1"
                            >
                              <RefreshCw size={11} />
                              {inst.sendRunState.kind === 'email' ? 'Resend' : 'Re-run'}
                            </button>
                          )}
                        </div>
                      )}
                      {rowError && (
                        <p className="mt-1 flex items-start gap-1.5 text-xs font-semibold text-rose-700">
                          <AlertCircle size={12} className="shrink-0 mt-px" />
                          <span>{rowError}</span>
                        </p>
                      )}
                      {inst.savedText && (
                        <p className="flex items-center gap-2">
                          <span className="font-medium text-slate-600 truncate">
                            “{inst.savedText.split('\n')[0]}”
                          </span>
                          <button
                            type="button"
                            onClick={() => openViewFor(inst)}
                            className="font-bold text-indigo-700 hover:text-indigo-900 cursor-pointer shrink-0 inline-flex items-center gap-1"
                          >
                            <Eye size={11} /> View
                          </button>
                        </p>
                      )}
                      {!inst.savedText && inst.savedFileName && (
                        <p className="flex items-center gap-2">
                          <span className="font-medium text-slate-600 truncate">{inst.savedFileName}</span>
                          <button
                            type="button"
                            onClick={() => openViewFor(inst)}
                            className="font-bold text-indigo-700 hover:text-indigo-900 cursor-pointer shrink-0 inline-flex items-center gap-1"
                          >
                            <Eye size={11} /> View
                          </button>
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {isCompleted && (
                  <button
                    type="button"
                    onClick={() => setRollbackInst(inst)}
                    className="p-1 text-slate-400 hover:text-amber-600 rounded-md hover:bg-amber-50 transition-colors cursor-pointer shrink-0"
                    title="Rollback action gate"
                    aria-label={`Rollback ${inst.title}`}
                  >
                    <RotateCcw size={13} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Send confirmation — nothing executes until Confirm. */}
      {(confirmInst || confirmLoading) && (
        <SendConfirmModal
          data={confirmData}
          loading={confirmLoading}
          loadError={confirmLoadError}
          confirming={confirming}
          onConfirm={handleConfirmSend}
          onCancel={closeSendConfirm}
        />
      )}

      {/* Content viewer (display text / files / saved notes). */}
      {viewState && (
        <ContentViewModal
          title={viewState.title}
          text={viewState.kind === 'text' ? viewState.text : undefined}
          file={viewState.kind === 'file' ? viewState.file : undefined}
          onClose={() => setViewState(null)}
        />
      )}

      {/* Note editor for record (text_input) tasks. */}
      {noteInst && (
        <NoteEditModal
          taskTitle={noteInst.title}
          initialText={textInputs[noteInst.id] || ''}
          saving={submittingId === noteInst.id}
          onSave={handleNoteSave}
          onCancel={() => setNoteInst(null)}
        />
      )}

      {/* Universal Task Action Confirmation Modal */}
      {actionConfirmInst && (
        <ActionTaskConfirmModal
          instance={actionConfirmInst}
          def={defById.get(actionConfirmInst.actionDefId)}
          applicantName={applicantName}
          recipientEmail={applicantEmail}
          confirming={submittingId === actionConfirmInst.id}
          emailTemplateNames={listNames?.emailTemplates}
          automationNames={listNames?.automations}
          onConfirm={async (payload) => {
            const extraFile = uploads[actionConfirmInst.id];
            const ok = await handleComplete(actionConfirmInst, {
              ...payload,
              ...(extraFile ? { savedFileUrl: extraFile.url, savedFileName: extraFile.name } : {}),
            });
            if (ok) setActionConfirmInst(null);
          }}
          onCancel={() => setActionConfirmInst(null)}
        />
      )}

      {/* Retrigger confirmation — queues a new backend run; the task
          stays completed. */}
      {retriggerInst && (
        <RetriggerConfirmModal
          data={retriggerDataFor(retriggerInst)}
          confirming={submittingId === retriggerInst.id}
          onConfirm={() => handleRetrigger(retriggerInst)}
          onCancel={() => setRetriggerInst(null)}
        />
      )}

      {/* Controlled Rollback Modal */}
      {rollbackInst && (
        <RollbackTaskModal
          instance={rollbackInst}
          reopening={submittingId === rollbackInst.id}
          onConfirmRollback={async (reason) => {
            await handleReopen(rollbackInst, reason);
          }}
          onCancel={() => setRollbackInst(null)}
        />
      )}
    </div>
  );
};
